/**
 * useEduVerify — hook for the .edu email verification flow.
 *
 * - status:   fetches `edu_verified` + saved `edu_email` from /api/verify/edu/status
 * - send:     posts /api/verify/edu/send (student-only, requires auth)
 * - confirm:  posts /api/verify/edu/confirm (public — token proves identity)
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export const useEduVerify = ({ auto = true } = {}) => {
  const [status, setStatus] = useState({ edu_verified: false, edu_email: null });
  const [loading, setLoading] = useState(auto);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/verify/edu/status");
      setStatus(res.data);
    } catch {
      setStatus({ edu_verified: false, edu_email: null });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (auto) refresh();
  }, [auto, refresh]);

  const send = useCallback(async (edu_email) => {
    const res = await client.post("/verify/edu/send", { edu_email });
    return res.data;
  }, []);

  const confirm = useCallback(async (token) => {
    const res = await client.post("/verify/edu/confirm", { token });
    return res.data;
  }, []);

  return { status, loading, refresh, send, confirm };
};
